<template>
  <div class="" v-loader.opacity.tab="loading">
    <div class="title-box">
      <h2 class="title">{{ $t('schedule.historyTitle') }}</h2>
      <p class="subtitle" v-if="schedule">{{ schedule.name }}</p>
    </div>

    <div class="clinic-schedule-history-box scrollbar">
      <BaseTable class="no-border" v-if="tableExecutions" :tabledata="tableExecutions">
        <template v-slot:column_trigger="{item}">
          <span>{{ triggerLabel(item.trigger) }}</span>
        </template>
        <template v-slot:column_period="{item}">
          <span>{{ periodLabel(item) }}</span>
        </template>
        <template v-slot:column_status="{item}">
          <span class="badge" :class="statusClass(item.status)">{{ statusLabel(item) }}</span>
          <span class="history-error" v-if="item.errorMessage" :title="item.errorMessage">{{ item.errorMessage }}</span>
        </template>
        <template v-slot:column_items="{item}">
          <span>{{ itemsLabel(item) }}</span>
          <span class="history-failed" v-if="item.failedCount">{{ $t('schedule.historyFailedCount', {count: item.failedCount}) }}</span>
        </template>
        <template v-slot:column_file="{item}">
          <span :title="item.remotePath || ''">{{ item.fileName || '-' }}</span>
        </template>
      </BaseTable>

      <p class="history-empty" v-if="!loading && !executions.length">{{ $t('schedule.historyEmpty') }}</p>

      <button class="btn btn-outline-primary btn-load-more"
              type="button"
              name="button"
              data-test="btnLoadMoreExecutions"
              v-if="executions.length < total"
              @click="loadMore">
        <span>{{ $t('schedule.historyLoadMore') }}</span>
      </button>
    </div>

    <div class="action-bar">
      <button class="btn btn-secondary" type="button" name="button" data-test="btnBack" @click="$emit('close-history')">
        <span>{{ $t('goBack') }}</span>
      </button>
      <button class="btn btn-outline-primary" type="button" name="button" data-test="btnRefreshHistory" @click="reload">
        <i class="fa fa-refresh"></i>
        <span>{{ $t('schedule.historyRefresh') }}</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.clinic-schedule-history-box {
  height:                       calc(100dvh - 394px);
  padding:                      10px;
}

.subtitle {
  color:                        var(--Shades-70);
}

.history-empty {
  color:                        var(--Shades-70);
  padding:                      20px 0;
  text-align:                   center;
}

.history-error,
.history-failed {
  color:                        var(--Shades-70);
  display:                      block;
  font-size:                    0.85em;
  overflow:                     hidden;
  text-overflow:                ellipsis;
  white-space:                  nowrap;
}

.btn-load-more {
  display:                      block;
  margin:                       10px auto;
}

@media (max-width: 991px) {
  .title-box {
    margin-bottom:              0;
  }

  .clinic-schedule-history-box {
    height:                     calc(100dvh - 309px);
    overflow-x:                 hidden;
    overflow-y:                 scroll;
  }
}

@media screen and (min-width: 992px) and (max-width: 1365px) {
  .clinic-schedule-history-box {
    height:                     calc(100dvh - 358px);
    overflow-x:                 hidden;
    overflow-y:                 scroll;
  }
}
</style>

<script>
import { executionStatusClass, executionStatusKey } from '@/utils/schedule-execution'

const PAGE_SIZE = 25

export default {
  name: 'ClinicScheduleHistory',
  props: ['clinic_id', 'schedule'],
  data: () => ({
    loading: false,
    executions: [],
    total: 0,
    tableExecutions: null,
  }),
  mounted() {
    this.reload()
  },
  methods: {
    reload: async function() {
      this.executions = []
      this.total = 0
      await this.fetchData()
    },
    loadMore: async function() {
      await this.fetchData()
    },
    fetchData: async function() {
      this.loading = true
      NProgress.start()
      try {
        let params = {
          clinicId: this.clinic_id,
          limit: PAGE_SIZE,
          skip: this.executions.length
        }
        if (this.schedule && this.schedule.uuid) {
          params.uuid = this.schedule.uuid
        }

        let response = await this.$clinicService.listScheduleExecutions(params)
        this.executions = this.executions.concat(response.result ? response.result : [])
        this.total = response.total ? response.total : this.executions.length

        this.tableExecutions = {
          'items': this.executions,
          'columns': [
            {name: this.$i18n.t('schedule.historyDate'), type: 'date', value: 'startedAt', width: '20%'},
            {name: this.$i18n.t('schedule.historyTrigger'), type: 'custom', key: 'trigger', width: '10%'},
            {name: this.$i18n.t('schedule.historyPeriod'), type: 'custom', key: 'period', width: '18%'},
            {name: this.$i18n.t('schedule.historyStatus'), type: 'custom', key: 'status', width: '22%'},
            {name: this.$i18n.t('schedule.historyItems'), type: 'custom', key: 'items', width: '12%'},
            {name: this.$i18n.t('schedule.historyFile'), type: 'custom', key: 'file', width: '18%'},
          ]
        }
      } catch (err) {
        console.log(err)
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    triggerLabel(trigger) {
      return trigger === 'manual'
        ? this.$i18n.t('schedule.historyTriggerManual')
        : this.$i18n.t('schedule.historyTriggerScheduled')
    },
    periodLabel(item) {
      if (item.details && item.details.notSynced) {
        return this.$i18n.t('schedule.notSyncedShort')
      }
      if (!item.startDate || !item.endDate) {
        return '-'
      }
      return `${item.startDate} - ${item.endDate}`
    },
    statusClass(status) {
      return executionStatusClass(status)
    },
    statusLabel(item) {
      return this.$i18n.t(executionStatusKey(item))
    },
    // reports generated (or claims exported) on this run
    itemsLabel(item) {
      if (item.status === 'failed') {
        return '-'
      }
      return String(item.itemCount || 0)
    },
  },
}
</script>
